import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useLayoutHistory } from "@/hooks/useLayoutHistory";
import { 
  DEFAULT_LAYOUT, 
  STORAGE_KEY, 
  VISIBLE_WIDGETS_KEY, 
  AVAILABLE_WIDGETS,
  LayoutItem 
} from "@/components/admin/DashboardWidgetConfig";

const ROW_DIRECTIONS_KEY = "admin-dashboard-row-directions";

export type RowDirections = Record<number, "horizontal" | "vertical">;

export function useDashboardLayout() {
  const [layout, setLayout] = useState<LayoutItem[]>([]);
  const [visibleWidgets, setVisibleWidgets] = useState<string[]>([]);
  const [rowDirections, setRowDirections] = useState<RowDirections>({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const { toast } = useToast();
  const isInitialLoadRef = useRef(true);
  
  const { 
    pushState, 
    undo: undoHistory, 
    redo: redoHistory, 
    canUndo, 
    canRedo,
    clearHistory 
  } = useLayoutHistory();

  // Check auth state and load layout
  useEffect(() => {
    const loadLayout = async () => {
      setIsLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      setUserId(user?.id || null);
      
      if (user) {
        // Try to load from database
        const { data, error } = await supabase
          .from("dashboard_layouts")
          .select("layout, visible_widgets, row_directions")
          .eq("user_id", user.id)
          .maybeSingle();
        
        if (data && !error) {
          const dbLayout = data.layout as unknown as LayoutItem[];
          const dbVisibleWidgets = data.visible_widgets as unknown as string[];
          const dbRowDirections = data.row_directions as unknown as RowDirections;
          setLayout(Array.isArray(dbLayout) ? dbLayout : DEFAULT_LAYOUT);
          setVisibleWidgets(Array.isArray(dbVisibleWidgets) ? dbVisibleWidgets : DEFAULT_LAYOUT.map(item => item.i));
          setRowDirections(typeof dbRowDirections === 'object' && dbRowDirections !== null ? dbRowDirections : {});
        } else {
          // Fall back to localStorage or default
          loadFromLocalStorage();
        }
      } else {
        // Not authenticated, use localStorage
        loadFromLocalStorage();
      }
      
      setIsLoading(false);
      // Mark initial load complete after a short delay to prevent initial state being pushed to history
      setTimeout(() => {
        isInitialLoadRef.current = false;
      }, 100);
    };

    const loadFromLocalStorage = () => {
      const savedLayout = localStorage.getItem(STORAGE_KEY);
      const savedVisibleWidgets = localStorage.getItem(VISIBLE_WIDGETS_KEY);
      const savedRowDirections = localStorage.getItem(ROW_DIRECTIONS_KEY);

      if (savedLayout) {
        try {
          setLayout(JSON.parse(savedLayout));
        } catch {
          setLayout(DEFAULT_LAYOUT);
        }
      } else {
        setLayout(DEFAULT_LAYOUT);
      }

      if (savedVisibleWidgets) {
        try {
          setVisibleWidgets(JSON.parse(savedVisibleWidgets));
        } catch {
          setVisibleWidgets(DEFAULT_LAYOUT.map(item => item.i));
        }
      } else {
        setVisibleWidgets(DEFAULT_LAYOUT.map(item => item.i));
      }

      if (savedRowDirections) {
        try {
          setRowDirections(JSON.parse(savedRowDirections));
        } catch {
          setRowDirections({});
        }
      } else {
        setRowDirections({});
      }
    };

    loadLayout();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" || event === "SIGNED_OUT") {
        loadLayout();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Save to database
  const saveToDatabase = useCallback(async (
    newLayout: LayoutItem[], 
    newVisibleWidgets: string[],
    newRowDirections: RowDirections
  ) => {
    if (!userId) return;

    // Check if record exists first
    const { data: existing } = await supabase
      .from("dashboard_layouts")
      .select("id")
      .eq("user_id", userId)
      .maybeSingle();

    // Cast layout to JSON-compatible format
    const layoutJson = JSON.parse(JSON.stringify(newLayout));
    const visibleWidgetsJson = JSON.parse(JSON.stringify(newVisibleWidgets));
    const rowDirectionsJson = JSON.parse(JSON.stringify(newRowDirections));

    if (existing) {
      // Update existing record
      await supabase
        .from("dashboard_layouts")
        .update({
          layout: layoutJson,
          visible_widgets: visibleWidgetsJson,
          row_directions: rowDirectionsJson,
        })
        .eq("user_id", userId);
    } else {
      // Insert new record
      await supabase
        .from("dashboard_layouts")
        .insert([{
          user_id: userId,
          layout: layoutJson,
          visible_widgets: visibleWidgetsJson,
          row_directions: rowDirectionsJson,
        }]);
    }
  }, [userId]);

  // Save to localStorage (for non-authenticated users or as fallback)
  const saveToLocalStorage = useCallback((
    newLayout: LayoutItem[], 
    newVisibleWidgets: string[],
    newRowDirections: RowDirections
  ) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newLayout));
    localStorage.setItem(VISIBLE_WIDGETS_KEY, JSON.stringify(newVisibleWidgets));
    localStorage.setItem(ROW_DIRECTIONS_KEY, JSON.stringify(newRowDirections));
  }, []);

  // Unified save function
  const persistLayout = useCallback((
    newLayout: LayoutItem[], 
    newVisibleWidgets: string[],
    newRowDirections: RowDirections
  ) => {
    if (userId) {
      saveToDatabase(newLayout, newVisibleWidgets, newRowDirections);
    } else {
      saveToLocalStorage(newLayout, newVisibleWidgets, newRowDirections);
    }
  }, [userId, saveToDatabase, saveToLocalStorage]);

  // Handle layout change with history tracking
  const handleLayoutChange = useCallback((newLayout: LayoutItem[]) => {
    if (!isInitialLoadRef.current) {
      pushState({ layout: newLayout, visibleWidgets });
    }
    setLayout(newLayout);
    persistLayout(newLayout, visibleWidgets, rowDirections);
  }, [persistLayout, visibleWidgets, rowDirections, pushState]);

  // Toggle row direction
  const toggleRowDirection = useCallback((rowIndex: number) => {
    const current = rowDirections[rowIndex] || "vertical";
    const newDirection: "horizontal" | "vertical" = current === "horizontal" ? "vertical" : "horizontal";
    const newRowDirections: RowDirections = {
      ...rowDirections,
      [rowIndex]: newDirection,
    };
    setRowDirections(newRowDirections);
    persistLayout(layout, visibleWidgets, newRowDirections);
  }, [rowDirections, layout, visibleWidgets, persistLayout]);

  // Add a widget with history tracking
  const addWidget = useCallback((widgetId: string) => {
    const widget = AVAILABLE_WIDGETS.find(w => w.id === widgetId);
    if (!widget || visibleWidgets.includes(widgetId)) return;

    const newVisibleWidgets = [...visibleWidgets, widgetId];

    // Find a position for the new widget
    const maxY = layout.length > 0 
      ? Math.max(...layout.map(item => item.y + item.h))
      : 0;

    const newLayoutItem: LayoutItem = {
      i: widgetId,
      x: 0,
      y: maxY,
      w: widget.defaultW,
      h: widget.defaultH,
      minW: widget.minW,
      minH: widget.minH,
    };

    const newLayout = [...layout, newLayoutItem];
    
    // Push to history before applying changes
    pushState({ layout: newLayout, visibleWidgets: newVisibleWidgets });
    
    setVisibleWidgets(newVisibleWidgets);
    setLayout(newLayout);
    persistLayout(newLayout, newVisibleWidgets, rowDirections);

    toast({
      title: "Widget added",
      description: `${widget.title} has been added to your dashboard.`,
    });
  }, [visibleWidgets, layout, rowDirections, persistLayout, toast, pushState]);

  // Remove a widget with history tracking
  const removeWidget = useCallback((widgetId: string) => {
    const widget = AVAILABLE_WIDGETS.find(w => w.id === widgetId);
    const newVisibleWidgets = visibleWidgets.filter(id => id !== widgetId);
    const newLayout = layout.filter(item => item.i !== widgetId);
    
    // Push to history before applying changes
    pushState({ layout: newLayout, visibleWidgets: newVisibleWidgets });
    
    setVisibleWidgets(newVisibleWidgets);
    setLayout(newLayout);
    persistLayout(newLayout, newVisibleWidgets, rowDirections);

    if (widget) {
      toast({
        title: "Widget removed",
        description: `${widget.title} has been removed from your dashboard.`,
      });
    }
  }, [visibleWidgets, layout, rowDirections, persistLayout, toast, pushState]);

  // Reset to default layout with history clear
  const resetLayout = useCallback(() => {
    setLayout(DEFAULT_LAYOUT);
    const defaultVisible = DEFAULT_LAYOUT.map(item => item.i);
    const defaultRowDirections: RowDirections = {};
    setVisibleWidgets(defaultVisible);
    setRowDirections(defaultRowDirections);
    persistLayout(DEFAULT_LAYOUT, defaultVisible, defaultRowDirections);
    clearHistory();

    toast({
      title: "Layout reset",
      description: "Your dashboard has been reset to the default layout.",
    });
  }, [persistLayout, toast, clearHistory]);

  // Undo layout change
  const undo = useCallback(() => {
    const previousState = undoHistory();
    if (previousState) {
      setLayout(previousState.layout);
      setVisibleWidgets(previousState.visibleWidgets);
      persistLayout(previousState.layout, previousState.visibleWidgets, rowDirections);
      toast({
        title: "Undone",
        description: "Layout change has been undone.",
      });
    }
  }, [undoHistory, rowDirections, persistLayout, toast]);

  // Redo layout change
  const redo = useCallback(() => {
    const nextState = redoHistory();
    if (nextState) {
      setLayout(nextState.layout);
      setVisibleWidgets(nextState.visibleWidgets);
      persistLayout(nextState.layout, nextState.visibleWidgets, rowDirections);
      toast({
        title: "Redone",
        description: "Layout change has been redone.",
      });
    }
  }, [redoHistory, rowDirections, persistLayout, toast]);

  // Get filtered layout for visible widgets only
  const filteredLayout = layout.filter(item => visibleWidgets.includes(item.i));

  return {
    layout: filteredLayout,
    visibleWidgets,
    rowDirections,
    isEditMode,
    isLoading,
    isSynced: !!userId,
    canUndo,
    canRedo,
    setIsEditMode,
    handleLayoutChange,
    toggleRowDirection,
    addWidget,
    removeWidget,
    resetLayout,
    undo,
    redo,
  };
}
